import { useState, useRef, useEffect } from "react";
import { Menu, Search, User, LogOut, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { applicationsAPI, renewalsAPI, contactAPI, paymentsAPI, reviewsAPI } from "../../services/api";

export function AdminHeader({ onMenuClick, sidebarCollapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [allData, setAllData] = useState({
    applications: [],
    renewals: [],
    contacts: [],
    payments: [],
    reviews: []
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const searchRef = useRef(null);

  // Fetch all data from APIs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appsRes, renewalsRes, contactsRes, paymentsRes, reviewsRes] = await Promise.all([
          applicationsAPI.getAll({ limit: 100 }),
          renewalsAPI.getAll({ limit: 100 }),
          contactAPI.getAll({ limit: 100 }),
          paymentsAPI.getAll({ limit: 100 }),
          reviewsAPI.getAll({ limit: 100 })
        ]);
        
        setAllData({
          applications: appsRes.data || [],
          renewals: renewalsRes.data || [],
          contacts: contactsRes.data || [],
          payments: paymentsRes.data || [],
          reviews: reviewsRes.data || []
        });
        setDataLoaded(true);
      } catch (error) {
        console.error("Error fetching search data:", error);
        setDataLoaded(true);
      }
    };
    
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search function - All data types
  useEffect(() => {
    if (searchQuery.length < 2 || !dataLoaded) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = [];

    // Search applications
    allData.applications.forEach(item => {
      if ((item.full_name || item.name || '').toLowerCase().includes(query) || 
          (item.application_number || '').toLowerCase().includes(query) ||
          (item.phone || '').toLowerCase().includes(query)) {
        results.push({ 
          ...item, 
          name: item.full_name || item.name,
          icon: "📄",
          typeLabel: "Application",
          detail: item.application_number,
          date: item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
          path: `/applications/${item.id}`
        });
      }
    });

    // Search renewals
    allData.renewals.forEach(item => {
      if ((item.member_name || item.full_name || '').toLowerCase().includes(query) || 
          (item.renewal_number || '').toLowerCase().includes(query) ||
          (item.member_phone || item.phone || '').toLowerCase().includes(query)) {
        results.push({ 
          ...item, 
          name: item.member_name || item.full_name,
          icon: "🔄",
          typeLabel: "Renewal",
          detail: item.renewal_number,
          date: item.renewal_date ? new Date(item.renewal_date).toLocaleDateString() : '',
          path: `/renew/${item.id}`
        });
      }
    });

    // Search contacts
    allData.contacts.forEach(item => {
      if ((item.name || '').toLowerCase().includes(query) || 
          (item.subject || '').toLowerCase().includes(query) ||
          (item.email || '').toLowerCase().includes(query)) {
        results.push({ 
          ...item, 
          icon: "💬",
          typeLabel: "Contact",
          detail: item.subject,
          date: item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
          path: "/contact-messages"
        });
      }
    });

    // Search payments
    allData.payments.forEach(item => {
      if ((item.name || '').toLowerCase().includes(query) || 
          (item.payment_number || '').toLowerCase().includes(query) ||
          (item.phone || '').toLowerCase().includes(query)) {
        results.push({ 
          ...item, 
          icon: "💳",
          typeLabel: "Payment",
          detail: item.payment_number,
          date: item.payment_date ? new Date(item.payment_date).toLocaleDateString() : '',
          path: "/payments"
        });
      }
    });

    // Search reviews
    allData.reviews.forEach(item => {
      if ((item.name || '').toLowerCase().includes(query) || 
          (item.role || '').toLowerCase().includes(query)) {
        results.push({ 
          ...item, 
          icon: "⭐",
          typeLabel: "Review",
          detail: item.role,
          date: item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
          path: "/reviews"
        });
      }
    });

    setSearchResults(results.slice(0, 8));
    setShowResults(true);
  }, [searchQuery, dataLoaded, allData]);

  const handleSelectResult = (path) => {
    setShowResults(false);
    setSearchQuery("");
    setTimeout(() => {
      navigate(path);
    }, 50);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setShowResults(false);
  };

  const handleLogoutClick = () => {
    logout();
    navigate("/login");
  };

  // Get status badge
  const getStatusBadge = (item) => {
    if (item.type === "Application") {
      switch(item.status) {
        case 'approved': return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Approved</span>;
        case 'pending': return <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">Pending</span>;
        case 'rejected': return <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">Rejected</span>;
        default: return null;
      }
    }
    if (item.type === "Renewal") {
      switch(item.status) {
        case 'active': return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">{item.daysLeft} days</span>;
        case 'expiring': return <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">{item.daysLeft} days</span>;
        case 'expired': return <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">Expired</span>;
        default: return null;
      }
    }
    if (item.type === "Contact") {
      switch(item.status) {
        case 'resolved': return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Resolved</span>;
        case 'in-progress': return <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">In Progress</span>;
        case 'new': return <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">New</span>;
        default: return null;
      }
    }
    return null;
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          {/* Search with Dropdown */}
          <div className="hidden md:block relative" ref={searchRef}>
            <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg w-96">
              <Search className="w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search applications, renewals, contacts..."
                className="bg-transparent border-none outline-none text-sm w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
              />
              {searchQuery && (
                <button onClick={clearSearch} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50">
                {searchResults.length > 0 ? (
                  <div className="py-2">
                    {searchResults.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectResult(result.path)}
                        className="w-full px-4 py-3 hover:bg-gray-50 flex items-start gap-3 text-left border-b last:border-0"
                      >
                        <span className="text-lg">{result.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <p className="font-medium text-[#111111]">
                                {result.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {result.typeLabel} • {result.detail}
                              </p>
                            </div>
                            {getStatusBadge(result)}
                          </div>
                          <p className="text-xs text-gray-500">
                            {result.date}
                          </p>
                        </div>
                      </button>
                    ))}
                    
                    {/* View all results link */}
                    <div className="p-2 border-t">
                      <button 
                        onClick={() => {
                          setShowResults(false);
                          setSearchQuery("");
                          navigate(`/search?q=${searchQuery}`);
                        }}
                        className="w-full text-center text-sm text-[#C46A1B] hover:underline py-1"
                      >
                        View all results ({searchResults.length})
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No results found for "{searchQuery}"</p>
                    <p className="text-xs mt-1">Try searching with different keywords</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          {/* Admin Profile */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.email || 'N/A'}</p>
              <p className="text-xs text-gray-500">{user?.fullName || 'N/A'}</p>
            </div>
            <div className="w-10 h-10 bg-[#C46A1B] rounded-full flex items-center justify-center text-white relative group">
              <User className="w-5 h-5" />
              <div className="absolute right-0 top-full mt-2 hidden group-hover:block">
                <button
                  onClick={handleLogoutClick}
                  className="bg-white shadow-lg rounded-lg py-2 px-4 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}